import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';

import * as sessionActions from '../../store/session';
import routerPath from '../../utils/router/routerPath';
import {
  isApexHost,
  sanitiseOrgCode,
  sessionFromMe,
  workspaceLoginUrl,
  writeTokens,
} from '../../utils/sessionBridge';

async function loginExtras() {
  const extras = {};
  try {
    const response = await fetch('/api/super_admin/login-security/client-config/');
    const cfg = await response.json();
    if (cfg.honeypot_enabled) {
      const field = document.getElementById('website_url');
      extras.website_url = field ? field.value : '';
    }
    if (cfg.timing_check_enabled && cfg.timing_token) {
      extras.form_timing = cfg.timing_token;
    }
  } catch {
    // Sign-in still sends a device id. The server ignores it when the check is off.
  }
  const key = 'taskydo.deviceId';
  let deviceId = '';
  try {
    deviceId = localStorage.getItem(key) || '';
    if (!deviceId) {
      deviceId = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `web-${Date.now()}`;
      localStorage.setItem(key, deviceId);
    }
  } catch {
    deviceId = `web-${Date.now()}`;
  }
  extras.device_id = deviceId;
  return extras;
}

/**
 * Same sign-in as Taskydo and Curedia: organisation code on the public
 * site, then email and password on the organisation host.
 */
export function PasswordRecovery() {
  const [params] = useSearchParams();
  const uid = (params.get('uid') || '').trim();
  const token = (params.get('token') || '').trim();
  const confirming = Boolean(uid && token);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState('');
  const [pending, setPending] = useState(false);

  async function onSubmit(event) {
    event.preventDefault();
    setPending(true);
    setMessage('');
    try {
      if (confirming) {
        if (password.length < 8) {
          setMessage('New password must be at least 8 characters.');
          return;
        }
        if (password !== confirm) {
          setMessage('The two passwords do not match.');
          return;
        }
        const response = await fetch('/api/super_admin/password-reset/confirm/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ uid, token, new_password: password }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.detail || 'This recovery link is not valid.');
        }
        setMessage(data.detail || 'Password updated. You can sign in.');
        return;
      }
      const response = await fetch('/api/super_admin/password-reset/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await response.json().catch(() => ({}));
      setMessage(data.detail || 'If an account exists for that email, we have sent password recovery instructions.');
    } catch (err) {
      setMessage(err.message || 'Could not send the recovery email.');
    } finally {
      setPending(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 420, mx: 'auto', mt: 8, px: 2 }}>
      <Typography variant="h4" gutterBottom>
        {confirming ? 'Choose a new password' : 'Reset password'}
      </Typography>
      <form onSubmit={onSubmit}>
        {confirming ? (
          <>
            <TextField
              fullWidth
              label="New password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Confirm password"
              type="password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              sx={{ mb: 2 }}
            />
          </>
        ) : (
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            sx={{ mb: 2 }}
          />
        )}
        <Button type="submit" variant="contained" fullWidth disabled={pending}>
          {pending ? 'Sending…' : confirming ? 'Update password' : 'Send recovery email'}
        </Button>
      </form>
      {message ? <Typography sx={{ mt: 2 }}>{message}</Typography> : null}
    </Box>
  );
}

export default function TaskydoLogin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sessionUser = useSelector((state) => state.session.user);
  const apex = isApexHost();
  const [organisation, setOrganisation] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  if (sessionUser && sessionUser.refresh) {
    return <Navigate to={routerPath.home} replace />;
  }

  function onOrganisation(event) {
    event.preventDefault();
    const slug = sanitiseOrgCode(organisation);
    if (!slug) {
      setError('Enter your organisation code, for example apple.');
      return;
    }
    window.location.assign(workspaceLoginUrl(slug));
  }

  async function onSubmit(event) {
    event.preventDefault();
    setPending(true);
    setError('');
    try {
      const extras = await loginExtras();
      const response = await fetch('/api/super_admin/login/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
          ...extras,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Could not sign in.');
      }
      const access = data.access;
      const refresh = data.refresh;
      const me = data.user || data;
      writeTokens(access, refresh);
      const session = sessionFromMe(me, { access, refresh });
      localStorage.setItem('user', JSON.stringify(session));
      dispatch(sessionActions.setUser(session));
      navigate(routerPath.home);
    } catch (err) {
      setError(err.message || 'Could not sign in.');
    } finally {
      setPending(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 420, mx: 'auto', mt: 8, px: 2 }}>
      <Typography variant="h4" gutterBottom>Letswebinar</Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        {apex
          ? 'Enter your organisation code. We will take you to that organisation’s sign-in page.'
          : 'Sign in with the same account you use for Taskydo and Curedia.'}
      </Typography>
      {apex ? (
        <form onSubmit={onOrganisation}>
          <TextField
            fullWidth
            label="Organisation code"
            value={organisation}
            onChange={(event) => setOrganisation(event.target.value)}
            placeholder="apple"
            sx={{ mb: 2 }}
          />
          <Button type="submit" variant="contained" fullWidth>Continue</Button>
        </form>
      ) : (
        <form onSubmit={onSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            sx={{ mb: 2 }}
            autoComplete="username"
          />
          <input id="website_url" name="website_url" tabIndex={-1} autoComplete="off" style={{ display: 'none' }} />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            sx={{ mb: 2 }}
            autoComplete="current-password"
          />
          <Button type="submit" variant="contained" fullWidth disabled={pending}>
            {pending ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      )}
      {error ? <Typography color="error" sx={{ mt: 2 }}>{error}</Typography> : null}
      <Button
        sx={{ mt: 2 }}
        onClick={() => { window.location.assign('/super_admin/signup'); }}
      >
        Create an organisation
      </Button>
      <Button
        sx={{ mt: 1 }}
        onClick={() => { window.location.assign('/password-recovery'); }}
      >
        Forgot password
      </Button>
    </Box>
  );
}
